// src/pages/CompanyDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companiesAPI, besoinsAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Plus, Trash2, Building2, Mail, Phone,
  Globe, MapPin, Users, Briefcase, MessageSquare, CheckCircle,
  ShieldCheck, UserPlus, UserMinus, Lock, Eye,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PhoneLink } from '../utils/helpers';
import TracabilityInfo from '../components/common/TracabilityInfo';


const ACTION_TYPES   = ['APPEL','EMAIL','REUNION','RELANCE','NOTE','AUTRE'];
const ACTION_STATUTS = ['A_FAIRE','EN_COURS','TERMINEE','ANNULE'];
const NEED_STATUS    = ['OUVERT','EN_COURS','FERME','PLACE'];
const CONTACT_STATUS = ['PROSPECT','INTERLOCUTEUR','DECISIONNAIRE','INACTIF','ANCIEN_CONTACT'];

// Statuts société avec couleurs
const COMPANY_STATUS_META = {
  CLIENT:                { label:'Client',                color:'#16A34A', bg:'rgba(34,197,94,0.08)' },
  ANCIEN_CLIENT:         { label:'Ancien client',         color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' },
  PROSPECT:              { label:'Prospect',              color:'#F59E0B', bg:'#FFFBEB' },
  PROSPECT_CHAUD:        { label:'Prospect chaud',        color:'#EA580C', bg:'#FFF7ED' },
  PARTENAIRE:            { label:'Partenaire',            color:'#FF4100', bg:'var(--orange-50)' },
  ANCIEN_PARTENAIRE:     { label:'Ancien partenaire',     color:'#94A3B8', bg:'var(--bg-elevated)' },
  FOURNISSEUR:           { label:'Fournisseur',           color:'#A78BFA', bg:'rgba(139,92,246,0.10)' },
  COLLABORATEUR_EXTERNE: { label:'Collab. externe',       color:'#06B6D4', bg:'rgba(8,145,178,0.10)' },
  ARCHIVE:               { label:'Archivé',               color:'var(--text-muted)', bg:'#F9FAFB' },
};
const COMPANY_STATUSES = Object.keys(COMPANY_STATUS_META);

// Statut action avec couleurs
const ACTION_STATUS_META = {
  A_FAIRE:  { label:'À faire',  color:'#F59E0B', bg:'#FFFBEB' },
  EN_COURS: { label:'En cours', color:'#FF4100', bg:'var(--orange-50)' },
  TERMINEE: { label:'Terminée', color:'#16A34A', bg:'rgba(34,197,94,0.08)' },
  ANNULE:   { label:'Annulé',   color:'#DC2626', bg:'rgba(239,68,68,0.12)' },
};


export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toUpperCase() === 'SUPER_ADMIN';
  const [company,       setCompany]      = useState(null);
  const [contacts,      setContacts]     = useState([]);
  const [needs,         setNeeds]         = useState([]);
  const [actions,       setActions]       = useState([]);
  const [companyAOs,    setCompanyAOs]    = useState([]);
  const [iaAccesses,    setIaAccesses]    = useState([]);
  const [iaList,        setIaList]        = useState([]);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [selectedIAs,   setSelectedIAs]   = useState([]);
  const [grantingSave,  setGrantingSave]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [csvBesoins, setCsvBesoins] = useState([]);


  // Formulaires inline
  const [showContactForm, setShowContactForm] = useState(false);
  const [showNeedForm,    setShowNeedForm]    = useState(false);
  const [showActionForm,  setShowActionForm]  = useState(false);
  const [newContact, setNewContact] = useState({ full_name:'', job_title:'', email:'', phone:'', status:'INTERLOCUTEUR' });
  const [newNeed,    setNewNeed]    = useState({ title:'', description:'', location:'', contract_type:'', tjm:'', status:'OUVERT' });
  const [newAction,  setNewAction]  = useState({ action_type:'NOTE', title:'', description:'', status:'A_FAIRE' });


  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, ctRes, nRes, aRes, aoRes, iaRes] = await Promise.all([
        companiesAPI.getOne(id),
        companiesAPI.listContacts(id),
        companiesAPI.listNeeds(id),
        companiesAPI.listActions(id),
        api.get(`/companies/${id}/aos`),
        api.get(`/companies/${id}/ia-access`).catch(() => ({ data: [] })),
      ]);
      setCompany(cRes.data);
      setContacts(ctRes.data || []);
      setNeeds(nRes.data || []);
      setActions(aRes.data || []);
      setCompanyAOs(aoRes.data || []);
      setIaAccesses(iaRes.data || []);

      // Charger les besoins CSV par nom de société
      const companyName = cRes.data?.name;
      if (companyName) {
        const bRes = await besoinsAPI.list({ societe: companyName, limit: 200 }).catch(() => ({ data: [] }));
        setCsvBesoins(bRes.data || []);
      }
    } catch { toast.error('Erreur chargement'); navigate('/companies'); }
    finally { setLoading(false); }
  };


  useEffect(() => { fetchAll(); }, [id]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/admin/users/ta-list').then(r => {
      setIaList((r.data || []).filter(u =>
        ['BUSINESS_MANAGER','INGENIEUR_AFFAIRES'].includes((u.role||'').toUpperCase())
      ));
    }).catch(() => {});
  }, [isSuperAdmin]);


  const handleAddContact = async () => {
    if (!newContact.full_name) { toast.error('Nom obligatoire'); return; }
    try {
      await companiesAPI.createContact(id, { ...newContact, company_id: id });
      setContacts(prev => [...prev, { ...newContact, id: Date.now() }]);
      setNewContact({ full_name:'', job_title:'', email:'', phone:'', status:'INTERLOCUTEUR' });
      setShowContactForm(false);
      toast.success('Contact ajouté'); fetchAll();
    } catch { toast.error('Erreur'); }
  };


  const handleAddNeed = async () => {
    if (!newNeed.title) { toast.error('Titre obligatoire'); return; }
    try {
      await companiesAPI.createNeed(id, { ...newNeed, company_id: id });
      setNewNeed({ title:'', description:'', location:'', contract_type:'', tjm:'', status:'OUVERT' });
      setShowNeedForm(false);
      toast.success('Besoin ajouté'); fetchAll();
    } catch { toast.error('Erreur'); }
  };


  const handleAddAction = async () => {
    if (!newAction.title) { toast.error('Titre obligatoire'); return; }
    try {
      await companiesAPI.createAction(id, { ...newAction, company_id: id });
      setNewAction({ action_type:'NOTE', title:'', description:'', status:'FAIT' });
      setShowActionForm(false);
      toast.success('Action ajoutée'); fetchAll();
    } catch { toast.error('Erreur'); }
  };


  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try { await companiesAPI.deleteContact(contactId); fetchAll(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };
  const handleDeleteNeed = async (needId) => {
    if (!window.confirm('Supprimer ce besoin ?')) return;
    try { await companiesAPI.deleteNeed(needId); fetchAll(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };
  const handleGrantAccess = async () => {
    if (!selectedIAs.length) { toast.error('Sélectionnez au moins un IA'); return; }
    setGrantingSave(true);
    try {
      await api.post(`/companies/${id}/ia-access`, { user_ids: selectedIAs });
      toast.success('Accès accordés');
      setSelectedIAs([]); setShowGrantForm(false); fetchAll();
    } catch { toast.error('Erreur'); }
    finally { setGrantingSave(false); }
  };

  const handleRevokeAccess = async (userId) => {
    if (!window.confirm('Révoquer cet accès ?')) return;
    try {
      await api.delete(`/companies/${id}/ia-access/${userId}`);
      toast.success('Accès révoqué'); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteAction = async (actionId) => {
    try { await companiesAPI.deleteAction(actionId); fetchAll(); }
    catch { toast.error('Erreur'); }
  };


  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}><div className="spinner"/>Chargement…</div>;
  if (!company) return null;


  const totalBesoins = needs.length + csvBesoins.length;

  const tabs = [
    { key:'overview',  label:'Aperçu' },
    { key:'contacts',  label:`Contacts (${contacts.length})` },
    { key:'needs',     label:`Besoins (${totalBesoins})` },
    { key:'actions',   label:`Actions (${actions.length})` },
    { key:'ia-access', label:`Accès IA (${iaAccesses.length})` },
  ];


  return (
    <div>
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/companies')}><ArrowLeft size={18}/></button>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:52, height:52, borderRadius:12, background:'var(--bg-elevated)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
              {company.logo_url
                ? <img src={company.logo_url} alt={company.name} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    style={{ width:'100%', height:'100%', objectFit:'contain', padding:4 }}/>
                : null}
              <Building2 size={22} color="#64748B" style={{ display: company.logo_url ? 'none' : 'block' }}/>
            </div>
            <div>
              <h1 style={{ margin:0 }}>{company.name}</h1>
              <p style={{ margin:0 }}>{company.sector || 'Secteur non renseigné'}</p>
            </div>
          </div>
        </div>
        <div className="page-header-actions">
          {(() => {
            const sm = COMPANY_STATUS_META[company.status] || { label:company.status, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
            return <span style={{ padding:'4px 14px', borderRadius:20, fontSize:'0.8125rem', fontWeight:700, background:sm.bg, color:sm.color }}>{sm.label}</span>;
          })()}
          <button className="btn btn-secondary" onClick={() => navigate(`/companies/${id}/edit`)}>
            <Edit2 size={14}/> Modifier
          </button>
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={company.created_at}
          createdById={company.owner_user_id}
          updatedAt={company.updated_at}
        />
      </div>


      {/* Tabs */}
      <div style={{ display:'flex', gap:4, borderBottom:'2px solid var(--border)', marginBottom:20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.875rem',
              background:'none', borderBottom: activeTab === t.key ? '2px solid #FF4100' : '2px solid transparent',
              color: activeTab === t.key ? '#FF4100' : 'var(--text-muted)', marginBottom:-2 }}>
            {t.label}
          </button>
        ))}
      </div>


      {/* Aperçu */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Informations générales</span></div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'4px 0' }}>
              {(company.address || company.city) && <InfoRow icon={<MapPin size={14}/>} label="Adresse" value={[company.address, company.postal_code, company.city, company.country].filter(Boolean).join(', ')}/>}
              {company.website && <InfoRow icon={<Globe size={14}/>} label="Site web" value={<a href={company.website} target="_blank" rel="noreferrer" style={{ color:'#FF4100' }}>{company.website}</a>}/>}
              {company.phone && <InfoRow icon={<Phone size={14}/>} label="Téléphone" value={<PhoneLink phone={company.phone} style={{ color:'#FF4100' }}/>}/>}
              {company.email && <InfoRow icon={<Mail size={14}/>} label="Email" value={company.email}/>}
              {company.siret && <InfoRow icon={<Briefcase size={14}/>} label="SIRET" value={company.siret}/>}
              {company.rcs && <InfoRow icon={<Briefcase size={14}/>} label="RCS" value={company.rcs}/>}
              {company.code_ape && <InfoRow icon={<Briefcase size={14}/>} label="Code APE" value={company.code_ape}/>}
              {company.tva_ic && <InfoRow icon={<Briefcase size={14}/>} label="TVA IC" value={company.tva_ic}/>}
              {company.legal_status && <InfoRow icon={<Briefcase size={14}/>} label="Statut juridique" value={company.legal_status}/>}
              {company.effectif && <InfoRow icon={<Users size={14}/>} label="Effectif" value={company.effectif}/>}
              {company.supplier_number && <InfoRow icon={<Briefcase size={14}/>} label="N° fournisseur" value={company.supplier_number}/>}
              {company.parent_company_name && <InfoRow icon={<Building2 size={14}/>} label="Société mère" value={company.parent_company_name}/>}
              {company.manager_name && <InfoRow icon={<Users size={14}/>} label="Account manager" value={company.manager_name}/>}
              {company.agency && <InfoRow icon={<MapPin size={14}/>} label="Agence" value={company.agency}/>}
              {company.provenance_type && <InfoRow icon={<MessageSquare size={14}/>} label="Provenance" value={[company.provenance_type, company.provenance_detail].filter(Boolean).join(' — ')}/>}
              {company.boond_ref && <InfoRow icon={<Briefcase size={14}/>} label="Réf. Boond" value={company.boond_ref}/>}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Notes</span></div>
            <p style={{ fontSize:'0.875rem', color: company.description ? 'var(--text-primary)' : 'var(--text-muted)', margin:0 }}>
              {company.description || 'Aucune description'}
            </p>
          </div>
        </div>
      )}


      {/* Contacts */}
      {activeTab === 'contacts' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Users size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Contacts ({contacts.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowContactForm(s => !s)}><Plus size={13}/> Ajouter</button>
          </div>
          {showContactForm && (
            <div style={{ padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:8, margin:'0 0 12px', border:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div><label style={lbl}>Nom complet *</label><input style={inp} value={newContact.full_name} onChange={e => setNewContact(f => ({...f, full_name:e.target.value}))} placeholder="Prénom Nom"/></div>
                <div><label style={lbl}>Poste</label><input style={inp} value={newContact.job_title} onChange={e => setNewContact(f => ({...f, job_title:e.target.value}))} placeholder="Directeur IT"/></div>
                <div><label style={lbl}>Email</label><input style={inp} type="email" value={newContact.email} onChange={e => setNewContact(f => ({...f, email:e.target.value}))}/></div>
                <div><label style={lbl}>Téléphone</label><input style={inp} value={newContact.phone} onChange={e => setNewContact(f => ({...f, phone:e.target.value}))}/></div>
                <div><label style={lbl}>Statut</label>
                  <select style={inp} value={newContact.status} onChange={e => setNewContact(f => ({...f, status:e.target.value}))}>
                    {CONTACT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowContactForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddContact}><CheckCircle size={13}/> Enregistrer</button>
              </div>
            </div>
          )}
          {contacts.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucun contact</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Nom</th><th>Poste</th><th>Statut</th><th>Contact</th><th></th></tr></thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight:600 }}>{c.full_name}</td>
                      <td style={{ color:'var(--text-muted)', fontSize:'0.8125rem' }}>{c.job_title || '—'}</td>
                      <td>{(() => {
                        const cm = { PROSPECT:{c:'#F59E0B',b:'#FFFBEB'}, INTERLOCUTEUR:{c:'#FF4100',b:'var(--orange-50)'}, DECISIONNAIRE:{c:'#7C3AED',b:'rgba(139,92,246,0.10)'}, INACTIF:{c:'var(--text-muted)',b:'rgba(255,255,255,0.06)'}, ANCIEN_CONTACT:{c:'#94A3B8',b:'var(--bg-elevated)'} };
                        const s = cm[c.status] || {c:'var(--text-muted)',b:'rgba(255,255,255,0.06)'};
                        return <span style={{ fontSize:'0.6875rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:s.b, color:s.c }}>{c.status}</span>;
                      })()}</td>
                      <td style={{ fontSize:'0.8125rem' }}>
                        {c.email && <div><Mail size={11} style={{ marginRight:4 }}/>{c.email}</div>}
                        {c.phone && <div><Phone size={11} style={{ marginRight:4 }}/><PhoneLink phone={c.phone} style={{ color:'var(--text-muted)', fontSize:'0.8125rem' }}/></div>}
                      </td>
                      <td><button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteContact(c.id)}><Trash2 size={13}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* Besoins */}
      {activeTab === 'needs' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Briefcase size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Besoins ({totalBesoins})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowNeedForm(s => !s)}><Plus size={13}/> Ajouter</button>
          </div>

          {/* Formulaire ajout besoin CRM */}
          {showNeedForm && (
            <div style={{ padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:8, margin:'0 0 12px', border:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Titre *</label><input style={inp} value={newNeed.title} onChange={e => setNewNeed(f => ({...f, title:e.target.value}))} placeholder="Développeur React Senior"/></div>
                <div><label style={lbl}>Localisation</label><input style={inp} value={newNeed.location} onChange={e => setNewNeed(f => ({...f, location:e.target.value}))}/></div>
                <div><label style={lbl}>TJM</label><input style={inp} value={newNeed.tjm} onChange={e => setNewNeed(f => ({...f, tjm:e.target.value}))} placeholder="600€/j"/></div>
                <div><label style={lbl}>Type contrat</label><input style={inp} value={newNeed.contract_type} onChange={e => setNewNeed(f => ({...f, contract_type:e.target.value}))}/></div>
                <div><label style={lbl}>Statut</label>
                  <select style={inp} value={newNeed.status} onChange={e => setNewNeed(f => ({...f, status:e.target.value}))}>
                    {NEED_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowNeedForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddNeed}><CheckCircle size={13}/> Enregistrer</button>
              </div>
            </div>
          )}

          {totalBesoins === 0 ? (
            <div style={{ textAlign:'center', padding:'32px', color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucun besoin pour cette société</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Réf. / Titre</th>
                    <th>État / Statut</th>
                    <th>Lieu</th>
                    <th>Démarrage / TJM</th>
                    <th>Responsable</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Besoins Boond/AO (CSV) — cliquables vers page détail */}
                  {csvBesoins.map(b => {
                    const etatColors = { 'Nouveau':'#3B82F6','En cour de traitement':'#F59E0B','Gagné':'#16A34A','Perdu':'#EF4444' };
                    const ec = etatColors[b.etat] || '#6B7280';
                    return (
                      <tr key={`boond-${b.id}`} style={{ cursor:'pointer' }} onClick={() => navigate(`/besoins/${b.id}`)}>
                        <td>
                          <span style={{ padding:'2px 8px', borderRadius:6, fontSize:'0.65rem', fontWeight:700, background: b.source === 'AO' ? 'rgba(255,65,0,0.12)' : 'rgba(56,189,248,0.10)', color: b.source === 'AO' ? '#FF6A38' : '#38BDF8', border:`1px solid ${b.source === 'AO' ? 'rgba(255,65,0,0.22)' : 'rgba(56,189,248,0.20)'}` }}>
                            {b.source === 'AO' ? 'AO' : 'Boond'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{b.titre || '—'}</div>
                          {b.reference && <div style={{ fontSize:'0.72rem', fontFamily:'monospace', color:'var(--text-muted)' }}>{b.reference}</div>}
                        </td>
                        <td><span style={{ padding:'2px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700, background:ec+'22', color:ec }}>{b.etat || '—'}</span></td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{b.lieu || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{b.date_demarrage || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{b.responsable_manager || '—'}</td>
                        <td><Eye size={13} color="var(--text-muted)"/></td>
                      </tr>
                    );
                  })}
                  {/* Besoins CRM (company_needs) */}
                  {needs.map(n => (
                    <tr key={`crm-${n.id}`}>
                      <td>
                        <span style={{ padding:'2px 8px', borderRadius:6, fontSize:'0.65rem', fontWeight:700, background:'rgba(167,139,250,0.12)', color:'#8B5CF6', border:'1px solid rgba(139,92,246,0.20)' }}>
                          CRM
                        </span>
                      </td>
                      <td style={{ fontWeight:600, fontSize:'0.875rem' }}>{n.title}</td>
                      <td>
                        <span style={{ padding:'2px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:600, color: n.status==='OUVERT'?'#16A34A':n.status==='PLACE'?'#FF4100':'var(--text-muted)', background: n.status==='OUVERT'?'rgba(34,197,94,0.08)':n.status==='PLACE'?'var(--orange-50)':'rgba(255,255,255,0.06)' }}>
                          {n.status}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{n.location || '—'}</td>
                      <td style={{ fontWeight:600, color:'#EA580C', fontSize:'0.8rem' }}>{n.tjm || '—'}</td>
                      <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{n.contract_type || '—'}</td>
                      <td><button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteNeed(n.id)}><Trash2 size={13}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* Actions CRM */}
      {activeTab === 'actions' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><MessageSquare size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Actions ({actions.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowActionForm(s => !s)}><Plus size={13}/> Ajouter</button>
          </div>
          {showActionForm && (
            <div style={{ padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:8, margin:'0 0 12px', border:'1px solid var(--border)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
                <div><label style={lbl}>Type</label>
                  <select style={inp} value={newAction.action_type} onChange={e => setNewAction(f => ({...f, action_type:e.target.value}))}>
                    {ACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Titre *</label><input style={inp} value={newAction.title} onChange={e => setNewAction(f => ({...f, title:e.target.value}))}/></div>
                <div><label style={lbl}>Statut</label>
                  <select style={inp} value={newAction.status} onChange={e => setNewAction(f => ({...f, status:e.target.value}))}>
                    {ACTION_STATUTS.map(s => <option key={s} value={s}>{ACTION_STATUS_META[s]?.label || s}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn:'1/-1' }}><label style={lbl}>Description</label>
                  <textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={newAction.description} onChange={e => setNewAction(f => ({...f, description:e.target.value}))}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowActionForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleAddAction}><CheckCircle size={13}/> Enregistrer</button>
              </div>
            </div>
          )}
          {actions.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.875rem' }}>Aucune action</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {actions.map(a => (
                <div key={a.id} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:8, display:'flex', alignItems:'flex-start', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.6875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>{a.action_type}</span>
                      {(() => {
                        const as = ACTION_STATUS_META[a.status] || { label:a.status, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                        return <span style={{ fontSize:'0.6875rem', fontWeight:700, padding:'1px 7px', borderRadius:10, background:as.bg, color:as.color }}>{as.label}</span>;
                      })()}
                      <span style={{ fontSize:'0.6875rem', color:'var(--text-muted)', marginLeft:'auto' }}>{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div style={{ fontWeight:600, fontSize:'0.8125rem' }}>{a.title}</div>
                    {a.description && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:3 }}>{a.description}</div>}
                  </div>
                  <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteAction(a.id)}><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Accès IA ──────────────────────────────────────── */}
      {activeTab === 'ia-access' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              <ShieldCheck size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>
              Ingénieurs d'Affaires ayant accès ({iaAccesses.length})
            </span>
            {isSuperAdmin && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowGrantForm(s => !s)}>
                <UserPlus size={13}/> Attribuer un accès
              </button>
            )}
          </div>

          {/* Formulaire d'attribution */}
          {isSuperAdmin && showGrantForm && (
            <div style={{ padding:'14px 16px', background:'var(--bg-elevated)', borderRadius:8, marginBottom:14, border:'1px solid var(--border)' }}>
              <label style={lbl}>Sélectionner les IA à autoriser</label>
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto', marginBottom:12 }}>
                {iaList.length === 0 ? (
                  <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>Aucun IA disponible</div>
                ) : iaList.map(u => {
                  const alreadyHas = iaAccesses.some(a => a.user_id === u.id);
                  const isSelected = selectedIAs.includes(u.id);
                  return (
                    <label key={u.id} style={{
                      display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                      border:`2px solid ${isSelected ? '#FF4100' : 'var(--border)'}`,
                      borderRadius:8, cursor: alreadyHas ? 'not-allowed' : 'pointer',
                      background: alreadyHas ? 'rgba(255,255,255,0.06)' : isSelected ? 'var(--orange-50)' : 'var(--bg-elevated)',
                      opacity: alreadyHas ? 0.6 : 1,
                    }}>
                      <input type="checkbox" checked={isSelected || alreadyHas}
                        disabled={alreadyHas}
                        onChange={() => {
                          if (alreadyHas) return;
                          setSelectedIAs(prev =>
                            prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id]
                          );
                        }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{u.prenom} {u.nom}</div>
                        <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{u.email}</div>
                      </div>
                      {alreadyHas && <span style={{ fontSize:'0.6875rem', color:'#16A34A', fontWeight:700 }}>✓ Déjà accès</span>}
                    </label>
                  );
                })}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowGrantForm(false); setSelectedIAs([]); }}>Annuler</button>
                <button className="btn btn-primary btn-sm" disabled={grantingSave || !selectedIAs.length} onClick={handleGrantAccess}>
                  {grantingSave ? <div className="spinner" style={{ width:12, height:12 }}/> : <ShieldCheck size={13}/>}
                  Accorder l'accès ({selectedIAs.length})
                </button>
              </div>
            </div>
          )}

          {iaAccesses.length === 0 ? (
            <div style={{ textAlign:'center', padding:'28px', color:'var(--text-muted)', fontSize:'0.875rem' }}>
              {isSuperAdmin
                ? 'Aucun IA autorisé — cliquez sur "Attribuer un accès" pour en ajouter.'
                : 'Aucun IA n\'a encore accès à cette société.'}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {iaAccesses.map(a => (
                <div key={a.access_id} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                  border:'1px solid var(--border)', borderRadius:8,
                }}>
                  <div className="avatar avatar-sm" style={{ background:'var(--orange-50)', color:'#FF4100', flexShrink:0 }}>
                    {(a.user_name||'?')[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{a.user_name}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{a.user_email}</div>
                  </div>
                  <span style={{ padding:'2px 8px', borderRadius:10, fontSize:'0.6875rem', fontWeight:600, background:'var(--orange-50)', color:'#FF4100' }}>
                    Ingénieur d'Affaires
                  </span>
                  {isSuperAdmin && (
                    <button className="btn btn-ghost btn-icon btn-sm" title="Révoquer l'accès"
                      style={{ color:'#DC2626' }} onClick={() => handleRevokeAccess(a.user_id)}>
                      <UserMinus size={14}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Positionnements supprimés de la vue société — voir /positionnements */}



    </div>
  );
}


function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
      <span style={{ color:'var(--text-muted)', flexShrink:0, marginTop:2 }}>{icon}</span>
      <div>
        <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:1 }}>{label}</div>
        <div style={{ fontSize:'0.875rem', color:'var(--text-primary)' }}>{value}</div>
      </div>
    </div>
  );
}


const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
